// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createStructuredSelector } from 'reselect';
import { connect, useSelector } from 'react-redux';
import { GroupTranslatorProvider, useGroupTranslator } from '../../../components/i18n/GroupTranslationContext';

// Utils
import { getElementStyle } from '../../../utils/grid/gridUtils';
import { elementClassNames, getDomElementId } from '../../utils/elementUtil';
import { getShouldApplyMediaOnlyDisplay, getShouldShowElementCaption } from '../../media/mediaElementDisplayUtils';
import {
    getElementId,
    getColor,
    getWidth,
    getCaption,
    getFileType,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getIsFileMediaVisible, hasFileData } from '../utils/fileElementUtils';
import { stopPropagationOnly } from '../../../utils/domUtil';
import { getShowQuickLineTool } from '../../quickLine/quickLineUtil';

// Selectors
import { currentBoardHasMediaDownloadAccessSelector } from '../../../utils/permissions/elementPermissionsSelector';
import { getIsReplaceModeHovered } from '../../../reducers/draggingSelector';

// Components
import ColorBar from '../../../components/colors/ColorBar';
import Caption from '../../../components/caption/Caption';
import ElementUserTag from '../../../user/userActivity/ElementUserTag';
import FileUploader from '../uploader/FileUploader';
import ElementResizeHandle from '../../resizing/ElementResizeHandle';
import ElementActivityIndicator from '../../activity/ElementActivityIndicator';
import QuickNotesActivityIndicator from '../../activity/QuickNotesActivityIndicator';
import ElementReactions from '../../reactions/ElementReactions';
import QuickLineCreationTool from '../../quickLine/QuickLineCreationTool';
import AttachedCommentsDropTarget from '../../dnd/elementDropTargets/AttachedCommentsDropTarget';
import AttachedCommentsContainer from '../../attachedComment/AttachedCommentsContainer';
import FileDetailViewContent from './FileDetailViewContent';
import MediaOverlayButton from '../../../components/mediaElementButtons/MediaOverlayButton';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../../common/elements/elementConstants';
import { FileTypes } from '../../../../common/files/fileTypes';

// Styles
import './FileDetailView.scss';

const mapStateToProps = () =>
    createStructuredSelector({
        showDownloadOption: currentBoardHasMediaDownloadAccessSelector,
    });

const FileDetailViewComponent = (props) => {
    const groupTranslate = useGroupTranslator();
    const {
        currentBoardId,
        elementId,
        element,
        inList,
        gridSize,
        tempSize,
        elementEvents,
        isLocked,
        attachment,
        isSelected,
        isEditable,
        isPresentational,
        className,
        permissions,
        setParentHoveredChildAcceptsDrop,
        getContextZoomScale,
        getContextZoomTranslationPx,
        openElementViewer,
    } = props;

    const colorName = getColor(element);
    const captionContent = getCaption(element);
    const captionVisible = getShouldShowElementCaption(props);

    const shouldDisplayMediaOnly = getShouldApplyMediaOnlyDisplay(props);
    const isVideoFile = getFileType(element) === FileTypes.VIDEO;
    const showMediaOverlayButton = shouldDisplayMediaOnly && !isVideoFile;

    const hasFile = hasFileData({ element, attachment });
    const isShowingMedia = getIsFileMediaVisible({ element, attachment });
    const showHandle = !isLocked && isShowingMedia && !inList && isEditable;

    const isReplaceModeHovered = useSelector((state) => getIsReplaceModeHovered(state, { elementId }));

    const savedElementWidthGU = getWidth(element);
    const elementWidthGU = isShowingMedia
        ? (tempSize && tempSize.width) || savedElementWidthGU || ELEMENT_DEFAULT_WIDTH
        : ELEMENT_DEFAULT_WIDTH;

    const fileClasses = classNames('FileDetailView drag-handle', className, {
        'contrast-shadow': !isShowingMedia,
    });
    const classes = elementClassNames(fileClasses, { ...props, isReplaceModeHovered });

    return (
        <div
            id={getDomElementId(getElementId(element))}
            className={classes}
            style={getElementStyle(elementWidthGU, gridSize, inList)}
            {...elementEvents}
        >
            {showMediaOverlayButton && <MediaOverlayButton onClick={openElementViewer} />}
            <ColorBar colorName={colorName} isSelected={isSelected} />
            <QuickLineCreationTool
                show={getShowQuickLineTool(props)}
                elementId={elementId}
                element={element}
                currentBoardId={currentBoardId}
                gridSize={gridSize}
                getContextZoomScale={getContextZoomScale}
                getContextZoomTranslationPx={getContextZoomTranslationPx}
            />
            <ElementActivityIndicator {...props} />
            <QuickNotesActivityIndicator {...props} />
            <ElementUserTag {...props} />

            {hasFile ? (
                <FileDetailViewContent {...props} shouldDisplayMediaOnly={shouldDisplayMediaOnly} />
            ) : (
                <FileUploader {...props} type="file" accept="*" message="Upload a file" />
            )}

            <Caption
                {...props}
                onDoubleClick={stopPropagationOnly}
                placeholder={groupTranslate('caption.placeholder', 'Add a description')}
                textContent={captionContent}
                captionVisible={captionVisible}
            />
            <ElementReactions {...props} />
            <AttachedCommentsDropTarget
                setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
                isRelative
                gridSize={gridSize}
                isPresentational={isPresentational}
                element={element}
                elementId={elementId}
                getContextZoomScale={getContextZoomScale}
            />
            <AttachedCommentsContainer
                elementId={elementId}
                element={element}
                gridSize={gridSize}
                isPresentational={isPresentational}
                currentBoardId={currentBoardId}
                permissions={permissions}
            />
            <ElementResizeHandle {...props} showHandle={showHandle} />
        </div>
    );
};

FileDetailViewComponent.propTypes = {
    currentBoardId: PropTypes.string,
    elementId: PropTypes.string,
    element: PropTypes.object.isRequired,
    attachment: PropTypes.object,
    inList: PropTypes.string,
    gridSize: PropTypes.number,
    elementEvents: PropTypes.object,
    tempSize: PropTypes.object,
    className: PropTypes.string,
    isLocked: PropTypes.bool,
    isSelected: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isEditable: PropTypes.bool,
    isEditing: PropTypes.bool,
    isPresentational: PropTypes.bool,
    permissions: PropTypes.number,
    handleDoubleClick: PropTypes.func,
    setParentHoveredChildAcceptsDrop: PropTypes.func,
    showDownloadOption: PropTypes.bool,
    getContextZoomScale: PropTypes.func,
    getContextZoomTranslationPx: PropTypes.func,
    openElementViewer: PropTypes.func,
};

const FileDetailView = (props) => {
    return (
        <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.file">
            <FileDetailViewComponent {...props} />
        </GroupTranslatorProvider>
    );
};

export default connect(mapStateToProps)(FileDetailView);
